/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback } from "react";
import { InteractiveAuth } from "matrix-js-sdk";
import {
  createClient,
  type LoginResponse,
  type MatrixClient,
} from "matrix-js-sdk";

import { initClient } from "../utils/matrix";
import { type Session } from "../ClientContext";
import { callTraceJournal, describeTraceError } from "../telemetry";
/**
 * This provides the login method to login using user credentials.
 * @param oldClient If there is an already authenticated client it should be passed to this hook
 * this allows the interactive login to sign out the client before logging in.
 * @returns A async method that can be called/awaited to log in with the provided credentials.
 */
export function useInteractiveLogin(
  oldClient?: MatrixClient,
): (
  homeserver: string,
  username: string,
  password: string,
) => Promise<[MatrixClient, Session]> {
  return useCallback<
    (
      homeserver: string,
      username: string,
      password: string,
    ) => Promise<[MatrixClient, Session]>
  >(
    async (homeserver: string, username: string, password: string) => {
      const startedAt = performance.now();
      const relatedEventId = callTraceJournal.record({
        eventType: "auth.login_started",
        stage: "auth",
        outcome: "started",
        payload: { homeserverOrigin: new URL(homeserver).origin },
      });
      const authClient = createClient({ baseUrl: homeserver });

      const interactiveAuth = new InteractiveAuth({
        matrixClient: authClient,
        doRequest: async (): Promise<LoginResponse> =>
          authClient.login("m.login.password", {
            identifier: {
              type: "m.id.user",
              user: username,
            },
            password,
          }),
        stateUpdated: (): void => {},
        requestEmailToken: async (): Promise<{ sid: string }> =>
          Promise.resolve({ sid: "" }),
      });

      // XXX: This claims to return an IAuthData which contains none of these
      // things - the js-sdk types may be wrong?
      /* eslint-disable camelcase,@typescript-eslint/no-explicit-any */
      let authData: any;
      try {
        authData = await interactiveAuth.attemptAuth();
      } catch (error) {
        callTraceJournal.record({
          eventType: "auth.login_failed",
          stage: "auth",
          severity: "error",
          outcome: "failed",
          relatedEventId,
          durationMs: Math.round(performance.now() - startedAt),
          payload: {
            operation: "interactive_login",
            ...describeTraceError(error),
          },
        });
        throw error;
      }
      const { user_id, access_token, device_id } = authData;
      const session = {
        user_id,
        access_token,
        device_id,
        passwordlessUser: false,
      };

      // To not confuse the rust crypto sessions we need to logout the old client before initializing the new one.
      let client: MatrixClient;
      try {
        await oldClient?.logout(true);
        client = await initClient(
          {
            baseUrl: homeserver,
            accessToken: access_token,
            userId: user_id,
            deviceId: device_id,
          },
          false,
        );
      } catch (error) {
        callTraceJournal.record({
          eventType: "auth.login_failed",
          stage: "auth",
          severity: "error",
          outcome: "failed",
          relatedEventId,
          durationMs: Math.round(performance.now() - startedAt),
          reasonCode: "client_initialization_failed",
          payload: {
            operation: "client_initialization",
            ...describeTraceError(error),
          },
        });
        throw error;
      }
      callTraceJournal.setIdentity(user_id, device_id);
      callTraceJournal.record({
        eventType: "auth.login_succeeded",
        stage: "auth",
        outcome: "succeeded",
        relatedEventId,
        durationMs: Math.round(performance.now() - startedAt),
      });
      /* eslint-enable camelcase */
      return [client, session];
    },
    [oldClient],
  );
}
